import {
  Component,
  computed,
  DestroyRef,
  DOCUMENT,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { BadgeModule } from '@openng/optimus-ui/badge';
import { AvatarModule } from '@openng/optimus-ui/avatar';
import { InputTextModule } from '@openng/optimus-ui/inputtext';

import { Menubar } from '@openng/optimus-ui/menubar';
import { MenuItem } from '@openng/optimus-ui/api';
import { translateObjectSignal } from '@jsverse/transloco';
import { AuthStore } from '@app/core/store/auth-store';
import { MessageModule } from '@openng/optimus-ui/message';
import { RouterLink } from '@angular/router';
import { AuthRoles } from '@app/core/models/auth-roles';
import { TranslocoPipe } from '@jsverse/transloco';
import { withStatusMessage } from '@app/core/services/status-message-interceptor';
import { ApplicationApi } from '../../../features/firstaid/shared/services/application-api';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, of } from 'rxjs';
import { CompanyStatus } from '../../models/company-status';
import { ApplicationStatus } from '../../../features/firstaid/shared/models/application-status';
import { Application } from '../../../features/firstaid/shared/models/application';
import { ProgressBarModule } from '@openng/optimus-ui/progressbar';
import { LoadingStore } from '@app/core/store/loading-store';
import { SKIP_LOADER } from '@app/core/services/loading-interceptor';
import {
  LanguageService,
  SUPPORTED_LANGUAGES,
} from '@app/core/services/language-service';
import { AppStore } from '@app/shared/store/app-store';
import { Company } from '@app/shared/models/company';

@Component({
  selector: 'app-navbar',
  imports: [
    Menubar,
    BadgeModule,
    AvatarModule,
    InputTextModule,
    MessageModule,
    RouterLink,
    TranslocoPipe,
    ProgressBarModule,
  ],
  templateUrl: './navbar.html',
  styleUrls: ['./navbar.scss'],
})
export class Navbar {
  private readonly EXTERNAL_STYLE_URL =
    'https://nothilfekurs-sehtest.fuehrerausweise.ch/wp-content/themes/nothilfkurse-sehtest/dist/kyberna-style.min.css';
  private document = inject(DOCUMENT);
  private externalStyleLink?: HTMLLinkElement;
  private destroyRef = inject(DestroyRef);
  private authStore = inject(AuthStore);
  private appStore = inject(AppStore);
  private applicationApi = inject(ApplicationApi);
  private languageService = inject(LanguageService);
  private translationSignal = translateObjectSignal('navbar');
  private companyTypeTranslationSignal = translateObjectSignal('companyType');
  protected loadingStore = inject(LoadingStore);

  items = signal<MenuItem[]>([]);
  applicationStatus = 'adjust';
  private info = signal<{
    company: Company | null;
    application: Application | null;
  }>({ company: null, application: null });

  ptMenubar = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    item: (item: any) => {
      return item.context.item.pt ?? undefined;
    },
  };

  protected readonly languages = SUPPORTED_LANGUAGES;

  protected isLoggedIn = computed(() => {
    const roles = this.authStore.roles();
    return !!roles && roles.length > 0;
  });

  protected currentLanguage = computed(() =>
    this.languageService.currentLanguage()
  );

  protected setLanguage(language: (typeof SUPPORTED_LANGUAGES)[number]): void {
    this.languageService.set(language);
  }

  protected login(): void {
    this.authStore.login('/auth-callback');
  }

  private toggleExternalStyle(loggedIn: boolean): void {
    if (loggedIn) {
      this.externalStyleLink?.remove();
      this.externalStyleLink = undefined;
      return;
    }

    const href = this.EXTERNAL_STYLE_URL;
    const existing = this.document.head.querySelector<HTMLLinkElement>(
      `link[rel="stylesheet"][href="${href}"]`
    );

    if (existing) {
      this.externalStyleLink = existing;
      return;
    }

    const link = this.document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    this.document.head.appendChild(link);
    this.externalStyleLink = link;
  }

  maintenanceMessage = computed(() => {
    const maintenanceMessage = this.appStore.maintenanceMessage();
    const language = this.languageService.currentLanguage();

    if (maintenanceMessage) {
      switch (language) {
        case 'it':
          return maintenanceMessage.message_it;
        case 'fr':
          return maintenanceMessage.message_fr;
        default:
          return maintenanceMessage.message_de;
      }
    }
    return null;
  });

  constructor() {
    // Load the external stylesheet only while the user is not logged in
    effect(() => {
      this.toggleExternalStyle(this.isLoggedIn());
    });

    // Effect 1: Build/update navbar items when relevant signals change
    effect(() => {
      this.authStore.roles();
      this.translationSignal();
      this.companyTypeTranslationSignal();
      this.info();
      this.appStore.accessibleRoutes();
      untracked(() => {
        this.updateNavbarItems();
      });
    });

    effect(() => {
      const roles = this.authStore.roles();
      const company = this.appStore.company();

      if (roles?.includes(AuthRoles.NHK_Admin)) {
        if (
          company?.status === CompanyStatus.QssPossible ||
          company?.status === CompanyStatus.QssExceeded ||
          company?.status === CompanyStatus.QssExpired
        ) {
          this.applicationApi
            .getLastCompanyOwn(
              undefined,
              withStatusMessage(true, false).set(SKIP_LOADER, true)
            )
            .pipe(
              takeUntilDestroyed(this.destroyRef),
              catchError(() => of(null))
            )
            .subscribe(application => {
              this.info.set({
                company: company,
                application,
              });
            });
        } else {
          this.info.set({
            company: company,
            application: null,
          });
        }
      }
    });
  }

  get userName(): string {
    const claims = this.authStore.identityClaims() || {};
    const username = claims['preferred_username'];

    if (typeof username === 'string' && username.length > 0) {
      return username;
    } else {
      return 'User';
    }
  }

  protected showRecertificationBanner() {
    const info = this.info();
    if (
      (info.company?.status === CompanyStatus.QssPossible ||
        info.company?.status === CompanyStatus.QssExceeded ||
        info.company?.status === CompanyStatus.QssExpired) &&
      (info.application?.status === ApplicationStatus.Done ||
        info.application?.status === ApplicationStatus.Declined)
    ) {
      return true;
    }
    return false;
  }

  private updateNavbarItems() {
    const items: MenuItem[] = this.getAllNavbarItems();
    const visibleItems = this.filterVisible(items);
    this.items.set(visibleItems);
  }

  private filterVisible(items: MenuItem[]): MenuItem[] {
    const result: MenuItem[] = [];
    const roles = this.authStore.roles();

    for (const item of items) {
      const children = Array.isArray(item.items) ? item.items : undefined;

      if (children && children.length) {
        const filteredChildren = this.filterVisible(children);
        // Keep parent if it has any visible children
        if (filteredChildren.length > 0) {
          result.push({
            ...item,
            items: filteredChildren,
          });
          continue;
        }
      } else {
        if (this.isVisible(item, roles)) {
          result.push(item);
        }
      }
    }

    return result;
  }

  private getAllNavbarItems(): MenuItem[] {
    const translations = this.translationSignal();
    const companyTypeTranslations = this.companyTypeTranslationSignal();

    const items = [
      {
        id: 'language',
        label: translations['language'],
        icon: 'pi pi-language',
        items: SUPPORTED_LANGUAGES.map(x => {
          return {
            id: `language.${x}`,
            label: x.toUpperCase(),
            command: () => {
              this.languageService.set(x);
            },
          };
        }),
      },
      {
        id: 'validityCheck',
        label: translations['validityCheck'],
        icon: 'pi pi-fw pi-check',
        routerLink: '/validityCheck',
      },
      {
        id: 'late-submission',
        label: translations['lateSubmission'],
        icon: 'pi pi-fw pi-file-import',
        routerLink: '/late-submission',
      },
      {
        id: 'dashboard',
        label: 'Dashboard',
        icon: 'pi pi-fw pi-home',
        routerLink: '/dashboard',
      },
      {
        id: 'vision',
        label: translations['vision'],
        icon: 'pi pi-eye',
        items: [
          {
            id: 'vision.Register',
            label: translations['visionRegistration'],
            icon: 'pi pi-id-card',
            routerLink: '/vision/registration',
          },
          {
            id: 'vision.Optician',
            label: translations['visionOptician'],
            icon: 'pi pi-building',
            routerLink: this.authStore.isAsaAdmin()
              ? '/vision/optician'
              : `/vision/optician/${this.appStore.companyId()}`,
          },
          {
            id: 'vision.Person',
            label: translations['visionPerson'],
            icon: 'pi pi-users',
            routerLink: '/vision/person',
          },
          {
            id: 'vision.late-submission',
            label: translations['visionlateSubmission'],
            icon: 'pi pi-file-import',
            routerLink: '/vision/late-submission',
          },
          {
            id: 'vision.test-result',
            label: translations['visionTestResult'],
            icon: 'pi pi-eye',
            routerLink: '/vision/test-result',
          },
        ],
      },
      {
        id: 'firstAid',
        label: translations['firstAid'],
        icon: 'pi pi-wave-pulse',
        items: [
          {
            id: 'firstAid.Application',
            label: translations['firstAidApplication'],
            icon: 'pi pi-list',
            routerLink: '/firstaid/application',
          },
          {
            id: 'firstAid.CourseProvider',
            label: translations['firstAidCourseProvider'],
            icon: 'pi pi-building',
            routerLink:
              this.authStore.isAsaAdmin() || this.authStore.isAstraAdmin()
                ? '/firstaid/courseprovider'
                : `/firstaid/courseprovider/${this.appStore.companyId()}`,
          },
          {
            id: 'firstAid.Registration',
            label: translations['firstAidRegistration'],
            icon: 'pi pi-id-card',
            routerLink: '/firstaid/registration',
            badge: this.showAdjustBadge() ? '!' : undefined,
          },
          {
            id: 'firstAid.Recertification',
            label: translations['firstAidRecertification'],
            icon: 'pi pi-id-card',
            routerLink: '/firstaid/recertification',
            badge: this.showAdjustBadge() ? '!' : undefined,
          },
          {
            id: 'firstAid.Person',
            label: translations['firstAidPerson'],
            icon: 'pi pi-users',
            routerLink: '/firstaid/person',
          },
          {
            id: 'firstAid.Courseaddress',
            label: translations['firstAidCourseaddress'],
            icon: 'pi pi-address-book',
            routerLink: '/firstaid/courseaddress',
          },
          {
            id: 'firstAid.Participant',
            label: translations['firstAidParticipant'],
            icon: 'pi pi-id-card',
            routerLink: '/firstaid/participant',
          },
          {
            id: 'firstAid.CourseGroup',
            label: translations['firstAidCourseGroup'],
            icon: 'pi pi-graduation-cap',
            routerLink: '/firstaid/coursegroup',
          },
          {
            id: 'firstAid.CourseType',
            label: translations['firstAidCourseType'],
            icon: 'pi pi-book',
            routerLink: '/firstaid/coursetype',
          },
          {
            id: 'firstaid.late-submission',
            label: translations['firstAidlateSubmission'],
            icon: 'pi pi-file-import',
            routerLink: '/firstaid/late-submission',
          },
        ],
      },
      {
        id: 'admin',
        label: translations['admin'],
        icon: 'pi pi-wrench',
        items: [
          {
            id: 'admin.auditlog',
            label: translations['adminAuditLog'],
            icon: 'pi pi-receipt',
            routerLink: '/admin/auditlog',
          },
          {
            id: 'admin.serviceStatusInfo',
            label: translations['adminServiceStatusInfo'],
            icon: 'pi pi-compass',
            routerLink: '/admin/status',
          },
          {
            id: 'admin.billingEvent',
            label: translations['adminBillingEvent'],
            icon: 'pi pi-money-bill',
            routerLink: '/admin/billing-event',
          },
          {
            id: 'admin.liechtensteinCertificate',
            label: translations['adminLiechtensteinCertificate'],
            icon: 'pi pi-eye',
            routerLink: '/admin/liechtenstein-certificate',
          },
          {
            id: 'admin.apiToken',
            label: translations['adminApiToken'],
            icon: 'pi pi-key',
            routerLink: '/admin/api-token',
          },
        ],
      },
      {
        id: 'register',
        label: translations['register'],
        icon: 'pi pi-user-plus',
        routerLink: '/signup',
      },
      {
        id: 'login',
        label: translations['login'],
        icon: 'pi pi-sign-in',
        command: () => {
          this.authStore.login('/auth-callback');
        },
      },
      {
        id: 'profile',
        label: this.appStore.company()?.name,
        icon: 'pi pi-user',
        pt: {
          'aria-label': translations['profile'],
        },
        items: [
          {
            id: 'profile.Logout',
            label: translations['logout'],
            icon: 'pi pi-sign-out',
            command: () => {
              this.authStore.logout();
            },
            linkClass: 'logout-item',
          },
          {
            id: 'profile.ProfileHeaderSeparator',
            separator: true,
          },
          {
            id: 'profile.ProfileHeader',
            label: translations['profile'],
            disabled: true,
            style: {
              fontWeight: 'bold',
            },
          },
          {
            id: 'profile.Profile',
            label: this.userName,
            icon: 'pi pi-user',
            routerLink: '/profile',
          },
        ],
      },
    ];

    if (
      (this.authStore.isNhkAdmin() ||
        this.authStore.isNhkUser() ||
        this.authStore.isVisionAdmin() ||
        this.authStore.isVisionUser()) &&
      this.appStore.companies().length > 0
    ) {
      const profileItem = items.find(x => x.id === 'profile');
      const profileSubItems = profileItem?.items as MenuItem[];

      profileSubItems.push(
        {
          id: 'profile.CompanyHeaderSeparator',
          separator: true,
        },
        {
          id: 'profile.CompanyHeader',
          label:
            this.authStore.isVisionAdmin() || this.authStore.isVisionUser()
              ? companyTypeTranslations['optician']
              : companyTypeTranslations['course_provider'],
          disabled: true,
          style: {
            fontWeight: 'bold',
          },
        }
      );

      this.appStore.companies().forEach(company => {
        profileSubItems.push({
          id: `profile.company${company.id}`,
          label: `${company.cost_center ? company.cost_center + ' ' : ''}${company.name}`,
          icon: 'pi pi-building',
          command: () => {
            this.appStore.updateCompanyId(company.id);
          },
        });
      });

      if (this.authStore.isVisionAdmin()) {
        profileSubItems.push({
          id: 'profile.CompanyNew',
          label: translations['profileCompanyNew'],
          icon: 'pi pi-plus',
          routerLink: '/vision/optician/new',
        });
      }
    }

    return items;
  }

  private isVisible(item: MenuItem, roles?: string[]): boolean {
    if (!this.appStore.isRouteAccessible(item.routerLink)) {
      return false;
    }

    if (roles && roles.length > 0) {
      switch (item.id) {
        case 'login':
        case 'validityCheck':
        case 'late-submission':
          return false;
      }
    } else {
      switch (item.id) {
        case 'profile.Logout':
        case 'profile.ProfileHeaderSeparator':
        case 'profile.ProfileHeader':
        case 'profile.CompanyHeaderSeparator':
        case 'profile.CompanyHeader':
          return false;
      }
    }

    return true;
  }

  private showAdjustBadge(): boolean {
    const info = this.info();
    if (!info.company && !info.application) {
      return true;
    } else if (
      info.application?.status === ApplicationStatus.Adjust ||
      info.application?.status === ApplicationStatus.Declined
    ) {
      return true;
    } else if (
      (info.company?.status === CompanyStatus.QssPossible ||
        info.company?.status === CompanyStatus.QssExceeded ||
        info.company?.status === CompanyStatus.QssExpired) &&
      info.application?.status === ApplicationStatus.Done
    ) {
      return true;
    } else if (info.company?.status === CompanyStatus.Revoked) {
      return true;
    } else {
      return false;
    }
  }
}
